/**
 * ARA Home OS — Editorial primitives.
 *
 * The interface almost disappears. Users notice the feeling, not the UI.
 *   · No cards. No rounded panels. No drop shadows. No gradients.
 *   · Hairlines instead of borders. Whitespace instead of dividers.
 *   · Fraunces display + Inter body. Charcoal ink on warm ivory.
 *   · Colour = meaning, never branding. Module dots only, sparingly.
 *
 * Inspired by John Pawson, Dieter Rams, Aesop, Kinfolk, Four Seasons.
 */

import { useEffect, useState } from "react";
import { ROOM_IDENTITY } from "../../data/roomIdentity";

/* ────────────────────────────────────────────────────────────────────
   useLightPhase — the residence's clock.
   Returns { phase, greeting, closingHint, hourLabel }.
   Recomputes every 5 minutes so the ambient tone glides through the day.
   ──────────────────────────────────────────────────────────────────── */
export function useLightPhase(name) {
  const compute = () => {
    const h = new Date().getHours();
    let phase, greeting, closingHint;
    if (h >= 5 && h < 8)       { phase = 'dawn';      greeting = 'The house is stirring';     closingHint = 'The day is only just beginning.'; }
    else if (h >= 8 && h < 12) { phase = 'morning';   greeting = 'Good morning';              closingHint = 'The house is otherwise calm.'; }
    else if (h >= 12 && h < 17){ phase = 'afternoon'; greeting = 'Good afternoon';            closingHint = 'Two things remain on the day.'; }
    else if (h >= 17 && h < 20){ phase = 'evening';   greeting = 'Good evening';              closingHint = 'The house is settling in.'; }
    else if (h >= 20 && h < 23){ phase = 'night';     greeting = 'The house is winding down'; closingHint = 'Only what asks for tomorrow.'; }
    else                       { phase = 'night';     greeting = 'The house is quiet';        closingHint = 'Sleep is the day\u2019s last chore.'; }
    if (name) greeting = `${greeting}, ${name}`;
    const hourLabel = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return { phase, greeting, closingHint, hourLabel };
  };
  const [state, setState] = useState(compute);
  useEffect(() => {
    const id = setInterval(() => setState(compute()), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [name]);
  return state;
}

/* ────────────────────────────────────────────────────────────────────
   Module accent palette (locked, sourced from tokens in index.css).
   Each module carries one honest tone — used ONLY as a small dot next
   to labels, never as a fill.
   ──────────────────────────────────────────────────────────────────── */
export const MODULE_TONE = {
  today:      'var(--ara-ink)',           // Charcoal only  — Today
  foyer:      'var(--ara-ink)',
  family:     'var(--ara-mod-contacts)',  // Warm Stone     — Family
  meals:      'var(--ara-mod-meals)',     // Soft Sage      — Meals
  kitchen:    'var(--ara-mod-meals)',
  shopping:   'var(--ara-mod-pantry)',    // Olive          — Shopping
  pantry:     'var(--ara-mod-pantry)',
  money:      'var(--ara-mod-money)',     // Deep Forest    — Money
  study:      'var(--ara-mod-money)',
  health:     'var(--ara-mod-health)',    // Eucalyptus     — Health
  wellness:   'var(--ara-mod-health)',
  documents:  'var(--ara-mod-documents)', // Slate          — Documents
  library:    'var(--ara-mod-documents)',
  home:       'var(--ara-mod-home)',      // Walnut         — Home
  cleaning:   'var(--ara-mod-cleaning)',  // Linen Sand     — Cleaning
  goals:      'var(--ara-mod-goals)',     // Brushed Brass  — Goals
  planning:   'var(--ara-mod-goals)',
  schedule:   'var(--ara-mod-schedule)',
  contacts:   'var(--ara-mod-contacts)',
  inventory:  'var(--ara-mod-inventory)',
  neutral:    'var(--ara-ink-soft)',
};

const tone = (t) => MODULE_TONE[t] || MODULE_TONE.neutral;

/* ────────────────────────────────────────────────────────────────────
   Editorial surface — the "room" everything sits in.
   No chrome. Just warm ivory and a narrow reading column.
   ──────────────────────────────────────────────────────────────────── */
export function EditorialSurface({ children, testid = "editorial-surface", className = "" }) {
  return (
    <div
      data-testid={testid}
      className={`min-h-screen font-sans-os text-ara-ink ${className}`}
      style={{ background: 'var(--ara-surface)' }}
    >
      {children}
    </div>
  );
}

export function EditorialColumn({ children, testid, className = "" }) {
  return (
    <div
      data-testid={testid}
      className={`mx-auto w-full max-w-[1120px] px-6 sm:px-10 lg:px-16 ${className}`}
    >
      {children}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   Rule — the OS's structural line.
   Default: warm stone hairline. Brass: reserved for occasional moments,
   like a gilded page inside a beautiful book.
   ──────────────────────────────────────────────────────────────────── */
export function Rule({ soft = false, brass = false, className = "", testid }) {
  const cls = brass ? 'rule-ara-brass' : (soft ? 'rule-ara-soft' : 'rule-ara');
  return (
    <hr
      data-testid={testid}
      className={`${cls} ${className}`}
    />
  );
}

/* ────────────────────────────────────────────────────────────────────
   Caption — quiet meta line (date, place, section wayfinding).
   ──────────────────────────────────────────────────────────────────── */
export function Caption({ children, className = "", testid, as: As = "p" }) {
  return (
    <As data-testid={testid} className={`caption-ara ${className}`}>{children}</As>
  );
}

/* ────────────────────────────────────────────────────────────────────
   ModuleMark — a small dot + module label. The ONLY place colour speaks.
   ──────────────────────────────────────────────────────────────────── */
export function ModuleMark({ module = "neutral", label, testid, className = "" }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-2 caption-ara ${className}`}
    >
      <span className="dot-ara" style={{ background: tone(module) }} aria-hidden />
      {label}
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────────
   PageHeading — the ONE focal point of every room.
   Structure: page title (Fraunces) + one sentence (Fraunces italic) +
   at most three primary actions. That's it. No summary line, no
   prepared-by-ARA whisper, no eyebrow paragraph.
   ──────────────────────────────────────────────────────────────────── */
/* ── Room Watercolour — decorative artwork behind a room's opening ── */
export function RoomWatercolour({ art }) {
  const cfg = ROOM_IDENTITY[art];
  if (!cfg) return null;
  return (
    <div
      aria-hidden="true"
      className="wc-art pointer-events-none select-none absolute top-0 bottom-0 right-0"
      style={{ width: cfg.width, zIndex: 0 }}
    >
      <img
        src={cfg.src}
        alt=""
        loading="eager"
        decoding="async"
        draggable="false"
        className="wc-art-img h-full w-full object-contain object-right-top"
        style={{ "--wc-opacity": cfg.opacity }}
      />
    </div>
  );
}

export function PageHeading({ module, room, title, focal, actions, art, testid = "page-heading" }) {
  return (
    <header data-testid={testid} className="relative pt-12 sm:pt-16 pb-10">
      {art && <RoomWatercolour art={art} />}
      <div className="relative" style={{ zIndex: 1 }}>
      {(module || room) && <ModuleMark module={module} label={room} className="mb-4" />}
      <h1
        className="display-ara text-[30px] sm:text-[38px] lg:text-[46px] leading-[1.05] tracking-[-0.015em] max-w-[18ch]"
        data-testid={`${testid}-title`}
      >
        {title}
      </h1>
      {focal && (
        <p
          className="mt-5 font-display text-[17px] sm:text-[19px] leading-[1.4] italic max-w-[38ch]"
          style={{ color: 'var(--ara-ink)', fontWeight: 400 }}
          data-testid={`${testid}-focal`}
        >
          {focal}
        </p>
      )}
      {actions && (
        <div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-3">
          {actions}
        </div>
      )}
      </div>
    </header>
  );
}

/* ────────────────────────────────────────────────────────────────────
   EditorialHero — retained for existing pages that pass standfirst/aside.
   New pages should prefer PageHeading.
   ──────────────────────────────────────────────────────────────────── */
export function EditorialHero({
  moduleMark,      // { module, label } — small dot + module name at the top
  meta,            // React node — date · place · weather
  title,           // Fraunces headline (moderate scale)
  standfirst,      // one short paragraph, max ~24 words
  aside,           // optional right-column note (small)
  testid = "editorial-hero",
}) {
  return (
    <header data-testid={testid} className="pt-12 sm:pt-16 pb-10 sm:pb-14">
      {moduleMark && <ModuleMark {...moduleMark} className="mb-4" />}
      {meta && !moduleMark && <div className="mb-4">{meta}</div>}

      <h1
        className="display-ara text-[30px] sm:text-[38px] lg:text-[46px] leading-[1.05] tracking-[-0.015em] max-w-[18ch]"
        data-testid={`${testid}-title`}
      >
        {title}
      </h1>

      {(standfirst || aside) && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-4">
          {standfirst && (
            <p
              className="md:col-span-7 lg:col-span-6 prose-ara text-[14.5px] max-w-[48ch]"
              data-testid={`${testid}-standfirst`}
            >
              {standfirst}
            </p>
          )}
          {aside && (
            <div className="md:col-span-5 lg:col-start-9 lg:col-span-4">
              {aside}
            </div>
          )}
        </div>
      )}

      {meta && moduleMark && (
        <div className="mt-8">{meta}</div>
      )}
    </header>
  );
}

/* ────────────────────────────────────────────────────────────────────
   EditorialSection — a titled section with quiet rhythm.
   Chief-of-staff mode: smaller title, minimal subtitle prose.
   ──────────────────────────────────────────────────────────────────── */
export function EditorialSection({
  moduleMark,
  title,
  subtitle,
  aside,
  children,
  testid,
  id,
  className = "",
}) {
  return (
    <section
      id={id}
      data-testid={testid}
      className={`pt-10 sm:pt-14 pb-2 ${className}`}
    >
      <Rule className="mb-10 sm:mb-12" />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-6">
        <div className="md:col-span-4 lg:col-span-3">
          {moduleMark && <ModuleMark {...moduleMark} className="mb-3" />}
          {title && (
            <h2
              className="text-[15.5px] leading-tight tracking-[-0.005em]"
              style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
            >
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="mt-3 prose-ara text-[13.5px] max-w-[32ch]">{subtitle}</p>
          )}
          {aside && <div className="mt-4">{aside}</div>}
        </div>
        <div className="md:col-span-8 lg:col-span-8 lg:col-start-5">
          {children}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────
   Figure — a stacked datum. No tile. No icon bubble.
   Tiny label, big Fraunces number, optional hairline + hint.
   ──────────────────────────────────────────────────────────────────── */
export function Figure({ label, value, hint, module = "neutral", align = "left", testid, className = "" }) {
  return (
    <div
      data-testid={testid}
      className={`${align === 'right' ? 'text-right' : ''} ${className}`}
    >
      <div className={`flex items-center gap-2 ${align === 'right' ? 'justify-end' : ''}`}>
        <span className="dot-ara" style={{ background: tone(module) }} aria-hidden />
        <p className="caption-ara">{label}</p>
      </div>
      <p
        className="mt-3 text-[22px] sm:text-[24px] leading-none tabular-nums"
        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
      >
        {value}
      </p>
      {hint && (
        <>
          <hr className="rule-ara-soft mt-5" />
          <p className="mt-3 prose-ara text-[13.5px]">{hint}</p>
        </>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   FigureRow — several figures separated by whitespace, not borders.
   ──────────────────────────────────────────────────────────────────── */
export function FigureRow({ items = [], testid = "figure-row" }) {
  return (
    <div
      data-testid={testid}
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-14 gap-y-12"
    >
      {items.map((f, i) => <Figure key={i} {...f} />)}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   TextLink — the OS's default action. Underlined Inter text.
   ──────────────────────────────────────────────────────────────────── */
export function TextLink({ children, onClick, href, testid, className = "", as: As }) {
  const Component = As || (href ? 'a' : 'button');
  return (
    <Component
      type={Component === 'button' ? 'button' : undefined}
      onClick={onClick}
      href={href}
      data-testid={testid}
      className={`link-ara ${className}`}
    >
      {children}
    </Component>
  );
}

/* ────────────────────────────────────────────────────────────────────
   PillButton — reserved for occasional primary/secondary calls.
   Hairline outline. No fill. No shadow.
   ──────────────────────────────────────────────────────────────────── */
export function PillButton({ children, onClick, type = "button", variant = "outline", disabled, testid, className = "" }) {
  const styles = {
    outline: { background: 'transparent',       color: 'var(--ara-ink)', border: '1px solid var(--ara-ink)' },
    ghost:   { background: 'transparent',       color: 'var(--ara-ink-muted)', border: '1px solid var(--ara-line)' },
    solid:   { background: 'var(--ara-ink)',    color: 'var(--ara-surface)', border: '1px solid var(--ara-ink)' },
  };
  const s = styles[variant] || styles.outline;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`inline-flex items-center gap-2 text-[12px] font-medium tracking-[0.16em] uppercase px-5 py-2.5 rounded-full transition-ara disabled:opacity-30 disabled:cursor-not-allowed hover:opacity-80 ${className}`}
      style={s}
    >
      {children}
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────────
   Tag — an inline chip made from a leading dot + small-caps text.
   NEVER filled. Used to denote module / state.
   ──────────────────────────────────────────────────────────────────── */
export function Tag({ children, module = "neutral", testid, className = "" }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-2 caption-ara ${className}`}
    >
      <span className="dot-ara" style={{ background: tone(module) }} aria-hidden />
      {children}
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────────
   ListItem — used inside sections (e.g. today's list, receipts).
   Row of: title (Fraunces), meta (Inter muted), trailing value. Hairline below.
   ──────────────────────────────────────────────────────────────────── */
export function ListItem({ title, meta, value, module, testid, onClick, last = false, className = "" }) {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left flex items-baseline gap-6 py-5 sm:py-6 ${last ? '' : 'border-b'} ${onClick ? 'transition-ara hover:opacity-70' : ''} ${className}`}
      style={{ borderColor: last ? 'transparent' : 'var(--ara-line-soft)' }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {module && <span className="dot-ara" style={{ background: tone(module) }} aria-hidden />}
          <p
            className="text-[15px] sm:text-[15.5px] leading-tight truncate"
            style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
          >
            {title}
          </p>
        </div>
        {meta && <p className="mt-1 prose-ara text-[13px]">{meta}</p>}
      </div>
      {value && (
        <p
          className="text-[15px] tabular-nums whitespace-nowrap"
          style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
        >
          {value}
        </p>
      )}
    </Wrapper>
  );
}

/* ────────────────────────────────────────────────────────────────────
   Standfirst — magazine-style intro paragraph.
   ──────────────────────────────────────────────────────────────────── */
export function Standfirst({ children, testid, className = "" }) {
  return (
    <p
      data-testid={testid}
      className={`font-display text-[22px] sm:text-[26px] leading-[1.4] tracking-[-0.01em] max-w-[42ch] ${className}`}
      style={{ color: 'var(--ara-ink)', fontWeight: 400 }}
    >
      {children}
    </p>
  );
}

/* ────────────────────────────────────────────────────────────────────
   Colophon — the discreet page footer signature.
   ──────────────────────────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────────────────────
   EmptyRoom — the Growing Home primitive.
   Every empty room shows: purpose · one guiding sentence · one action.
   No fake data. No metrics. No dashboards. Just an invitation.
   ──────────────────────────────────────────────────────────────────── */
export function EmptyRoom({
  purpose,          // small tracked-caption line above the sentence
  sentence,         // one Fraunces-italic invitation
  actionLabel,      // solid pill button label
  onAction,         // click handler for the pill
  testid = "empty-room",
}) {
  return (
    <div
      data-testid={testid}
      className="pt-6 sm:pt-10 pb-16 max-w-[520px]"
    >
      {purpose && (
        <p
          className="caption-ara mb-4"
          style={{ letterSpacing: '0.16em', color: 'var(--ara-ink-soft)' }}
        >
          {purpose}
        </p>
      )}
      {sentence && (
        <p
          className="font-display text-[18px] sm:text-[20px] leading-[1.4] italic"
          style={{ color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '38ch' }}
          data-testid={`${testid}-sentence`}
        >
          {sentence}
        </p>
      )}
      {actionLabel && onAction && (
        <div className="mt-8">
          <PillButton variant="solid" onClick={onAction} testid={`${testid}-action`}>
            {actionLabel}
          </PillButton>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   FirstFiveWhisper — the quiet celebration after all five firsts land.
   ──────────────────────────────────────────────────────────────────── */
export function FirstFiveWhisper({ testid = "first-five-whisper" }) {
  return (
    <div
      data-testid={testid}
      className="pt-8 pb-6 max-w-[520px]"
    >
      <div className="rule-ara-brass mb-6" />
      <p
        className="font-display text-[17px] sm:text-[18px] leading-[1.4] italic"
        style={{ color: 'var(--ara-ink)', fontWeight: 400 }}
      >
        Your Residence is beginning to take shape.
      </p>
      <div className="rule-ara-brass mt-6" />
    </div>
  );
}


export function Colophon({ children = "ARA · Household operating system", testid = "colophon", className = "" }) {
  return (
    <div className={`pt-16 pb-14 ${className}`} data-testid={testid}>
      <Rule className="mb-10" />
      <p className="caption-ara">{children}</p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   UnderlineInput — hairline-only field.
   Label sits above in the tracked caption. The field itself is a
   transparent line drawn along the bottom. On focus, the hairline
   thickens and darkens to charcoal.
   ──────────────────────────────────────────────────────────────────── */
export function UnderlineInput({
  id, label, hint,
  type = "text",
  value, onChange,
  placeholder, autoComplete,
  required, minLength,
  testid,
  className = "",
}) {
  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="caption-ara block mb-3">{label}</label>
      )}
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
        data-testid={testid}
        className="w-full bg-transparent outline-none py-3 text-[17px] tracking-[-0.005em]"

        style={{
          color: 'var(--ara-ink)',
          borderBottom: '1px solid var(--ara-line)',
          transition: 'border-color 260ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
        onFocus={(e) => { e.currentTarget.style.borderBottom = '1px solid var(--ara-ink)'; }}
        onBlur={(e) => { e.currentTarget.style.borderBottom = '1px solid var(--ara-line)'; }}
      />
      {hint && <p className="mt-2 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>{hint}</p>}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   AuthShell — the residence's foyer.
   Two-column ivory canvas. Left: welcome + form. Right: editorial preview.
   A single vertical stone hairline separates the two.
   ──────────────────────────────────────────────────────────────────── */
export function AuthShell({ children, preview, testid = "auth-shell", phase }) {
  return (
    <div data-phase={phase} data-testid={`${testid}-phase`}>
      <EditorialSurface testid={testid} className="min-h-screen">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-16 min-h-screen">
          <div className="lg:col-span-6 xl:col-span-5 flex flex-col justify-center py-16 lg:py-24">
            {children}
          </div>
          <div
            className="hidden lg:flex lg:col-span-6 xl:col-span-6 xl:col-start-7 lg:pl-16 flex-col justify-center border-l"
            style={{ borderColor: 'var(--ara-line)' }}
            data-testid="auth-preview"
          >
            {preview}
          </div>
        </div>
      </EditorialSurface>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   Masthead — the residence's identity plate at the top of the left rail.
   ──────────────────────────────────────────────────────────────────── */
export function Masthead({ residence = "The Residence", volume, dateLabel, testid = "residence-masthead", className = "" }) {
  return (
    <div data-testid={testid} className={className}>
      <p className="caption-ara mb-3">ARA</p>
      <h2
        className="font-display text-[24px] leading-tight"
        style={{ fontWeight: 400, color: 'var(--ara-ink)', letterSpacing: '-0.01em' }}
      >
        {residence}
      </h2>
      {(volume || dateLabel) && (
        <p className="mt-3 caption-ara" style={{ letterSpacing: '0.16em' }}>
          {volume}{volume && dateLabel ? ' · ' : ''}{dateLabel}
        </p>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   RoomNav — the residence's room directory.
   Each room = one text label + one hairline. Current room marked
   by a single 6px dot in the room's signature tone. No pills, no fills.
   ──────────────────────────────────────────────────────────────────── */
export function RoomNav({ rooms = [], currentKey, onNavigate, testid = "residence-rooms", className = "" }) {
  return (
    <nav data-testid={testid} className={className} aria-label="Rooms of the residence">
      {rooms.map((room, i) => {
        const isCurrent = room.key === currentKey;
        const El = onNavigate ? 'button' : 'div';
        return (
          <El
            key={room.key || i}
            type={onNavigate ? 'button' : undefined}
            onClick={onNavigate ? () => onNavigate(room.key) : undefined}
            data-testid={`room-${room.key}`}
            className="w-full text-left flex items-center gap-3 py-2.5 transition-ara"
            aria-current={isCurrent ? 'page' : undefined}
          >
            <span
              className="dot-ara transition-ara"
              style={{
                background: isCurrent ? (MODULE_TONE[room.tone] || MODULE_TONE.neutral) : 'transparent',
                border: isCurrent ? 'none' : `1px solid var(--ara-line)`,
              }}
              aria-hidden
            />
            <span
              className="flex-1 min-w-0"
              style={{
                color: isCurrent ? 'var(--ara-ink)' : 'var(--ara-ink-muted)',
                fontFamily: 'Inter, system-ui, sans-serif',
              }}
            >
              <span
                className="block text-[14px] leading-tight truncate"
                style={{ fontWeight: isCurrent ? 500 : 400 }}
              >
                {room.label}
              </span>
            </span>
          </El>
        );
      })}
    </nav>
  );
}

/* ────────────────────────────────────────────────────────────────────
   ResidenceLayout — the two-column residence chrome.
   Desktop (lg+): sticky left rail (260px) + reading column.
   Tablet/phone: rail collapses into a slide-down drawer opened by a
   small "Rooms" pill in the top bar. Content column takes full width
   with generous margins.
   ──────────────────────────────────────────────────────────────────── */
export function ResidenceLayout({ rail, children, phase, wide = false, testid = "residence-layout" }) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div data-phase={phase} data-testid={`${testid}-phase`}>
      <EditorialSurface testid={testid}>
        {/* Mobile top bar (hidden on lg+) */}
        <div
          className="lg:hidden sticky top-0 z-30 backdrop-blur-sm flex items-center justify-between px-5 py-3"
          style={{
            background: 'rgba(250, 249, 246, 0.9)',
            borderBottom: '1px solid var(--ara-line)',
          }}
          data-testid="residence-mobile-bar"
        >
          <p
            className="text-[11px] tracking-[0.24em] uppercase"
            style={{ color: 'var(--ara-ink-soft)', fontWeight: 500 }}
          >
            ARA
          </p>
          <button
            type="button"
            onClick={() => setDrawerOpen(o => !o)}
            className="text-[12px] tracking-[0.14em] uppercase px-4 py-2 rounded-full"
            style={{
              color: 'var(--ara-ink)',
              border: '1px solid var(--ara-ink)',
              fontWeight: 500,
              minHeight: '44px',
              minWidth: '44px',
            }}
            aria-expanded={drawerOpen}
            aria-label="Rooms menu"
            data-testid="residence-mobile-rooms-toggle"
          >
            {drawerOpen ? 'Close' : 'Rooms'}
          </button>
        </div>

        {/* Mobile drawer */}
        {drawerOpen && (
          <div
            className="lg:hidden px-6 py-8"
            style={{
              background: 'var(--ara-surface)',
              borderBottom: '1px solid var(--ara-line)',
            }}
            data-testid="residence-mobile-drawer"
            onClick={(e) => {
              // Close drawer when a room link is tapped
              if (e.target.closest('[data-testid^="room-"]') || e.target.closest('[data-testid="rail-ask-ara"]')) {
                setDrawerOpen(false);
              }
            }}
          >
            {rail}
          </div>
        )}

        <div className="mx-auto max-w-[1360px] px-6 sm:px-10 lg:px-14 flex flex-col lg:flex-row gap-0 lg:gap-16">
          <aside
            className="hidden lg:block lg:sticky lg:top-0 lg:h-screen lg:w-[260px] lg:flex-shrink-0 py-12 lg:py-16 lg:pr-16 lg:-mr-16 lg:border-r"
            style={{ borderColor: 'var(--ara-line)' }}
            data-testid="residence-rail"
          >
            {rail}
          </aside>
          <div className={`flex-1 min-w-0 ${wide ? 'max-w-[1080px]' : 'max-w-[820px]'} py-6 lg:py-16 lg:pl-16 pb-24 lg:pb-16`}>
            {children}
          </div>
        </div>
      </EditorialSurface>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   HouseholdPulse — a whisper-thin line of live status.
   items: [{ label, live?: bool, module?, tone? }]
   Live items carry a gently breathing dot.
   ──────────────────────────────────────────────────────────────────── */
export function HouseholdPulse({ items = [], testid = "household-pulse", className = "" }) {
  if (!items.length) return null;
  return (
    <p
      data-testid={testid}
      className={`inline-flex items-center gap-x-5 gap-y-2 flex-wrap caption-ara ${className}`}
      style={{ letterSpacing: '0.14em' }}
    >
      {items.map((it, i) => (
        <span key={i} className="inline-flex items-center gap-2">
          {i > 0 && <span aria-hidden style={{ color: 'var(--ara-ink-soft)' }}>·</span>}
          <span
            className={`dot-ara ${it.live ? 'dot-pulse' : ''}`}
            style={{ background: MODULE_TONE[it.tone || it.module || 'neutral'] || MODULE_TONE.neutral }}
            aria-hidden
          />
          <span>{it.label}</span>
        </span>
      ))}
    </p>
  );
}

/* ────────────────────────────────────────────────────────────────────
   MorningBrief — the residence's cover.
   Dated address, one large sentence, everything else quietly supports.
   ──────────────────────────────────────────────────────────────────── */
export function MorningBrief({
  dated,           // e.g. "Sunday · 16 February · Auckland"
  resident,        // e.g. italic greeting slug — "Good morning, Erana."
  pulse,           // <HouseholdPulse ... /> or any node — living status
  headline,        // the one thing
  standfirst,      // the one supporting paragraph
  items = [],      // numbered brief items — { time, module, title, meta }
  closing,         // e.g. "The house is otherwise calm."
  testid = "morning-brief",
}) {
  return (
    <article data-testid={testid}>
      {dated && <p className="caption-ara rise-ara" data-testid="brief-dated">{dated}</p>}
      {resident && (
        <p
          className="mt-6 font-display text-[17px] italic rise-ara rise-ara-s1"
          style={{ color: 'var(--ara-ink)', fontWeight: 400 }}
          data-testid="brief-resident"
        >
          {resident}
        </p>
      )}
      {pulse && <div className="mt-6 rise-ara rise-ara-s2" data-testid="brief-pulse">{pulse}</div>}

      <h1
        className="display-ara mt-8 text-[32px] sm:text-[42px] lg:text-[52px] leading-[1.05] max-w-[22ch] rise-ara rise-ara-s3"
        data-testid="brief-headline"
      >
        {headline}
      </h1>

      {standfirst && (
        <p
          className="mt-6 prose-ara text-[15px] sm:text-[15.5px] max-w-[52ch] rise-ara rise-ara-s4"
          data-testid="brief-standfirst"
        >
          {standfirst}
        </p>
      )}

      {items.length > 0 && (
        <ol className="mt-10 space-y-0" data-testid="brief-items">
          {items.map((item, i) => (
            <li
              key={i}
              className={`grid grid-cols-[auto_1fr] gap-x-6 py-5 ${i === 0 ? 'border-t' : ''} border-b rise-ara rise-ara-s${Math.min(5 + i, 10)}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`brief-item-${i}`}
            >
              <span
                className="font-display text-[14px] tabular-nums"
                style={{ color: 'var(--ara-ink-soft)', fontWeight: 400 }}
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              <div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  {item.time && (
                    <span className="font-display text-[15px] tabular-nums" style={{ color: 'var(--ara-ink)' }}>
                      {item.time}
                    </span>
                  )}
                  <ModuleMark module={item.module || 'neutral'} label={item.moduleLabel || ''} />
                </div>
                <p className="mt-1.5 font-display text-[17px] sm:text-[19px] leading-snug tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                  {item.title}
                </p>
                {item.meta && <p className="mt-1 prose-ara text-[13px]">{item.meta}</p>}
              </div>
            </li>
          ))}
        </ol>
      )}

      {closing && (
        <p
          className="mt-10 font-display text-[15px] italic max-w-[48ch] rise-ara"
          style={{ color: 'var(--ara-ink-muted)', fontWeight: 400, animationDelay: `${Math.min(800, 500 + items.length * 80)}ms` }}
          data-testid="brief-closing"
        >
          — {closing}
        </p>
      )}
    </article>
  );
}
